import { format, isMatch, parse } from "date-fns";
import { Locale } from "date-fns/locale";
import { de } from "date-fns/locale/de";

// Date/time formatters shared by both apps. Each takes an optional date-fns locale (default German);
// callers that support multiple languages resolve their language to a locale (e.g. locales[language])
// and pass it through, so these helpers stay locale-agnostic and don't depend on any app's LanguageType.

/** Date and time as the app shows a single point in time, e.g. "14.10.2026 11:27"; pass the locale along. */
export const DATE_TIME_FORMAT = "dd.MM.yyyy HH:mm";

/** Long localized date + time, e.g. "29. April 2026 um 14:30:00". `pattern` accepts any date-fns format. */
export const formatDateTime = (date: Date | number, pattern = "Ppp", locale: Locale = de): string =>
  format(date, pattern, { locale });

/** Time of day, 24-hour, e.g. "07:00". */
export function formatTime(date: Date, locale: Locale = de): string {
  return format(date, "HH:mm", { locale });
}

/** Date only, using the locale's short-date format (German: "22.06.2026"). */
export function formatShortDate(date: Date, locale: Locale = de): string {
  return format(date, "P", { locale });
}

/** True when the string is a complete short date in the locale's format (German: "22.06.2026"). */
export function isShortDate(value: string, locale: Locale = de): boolean {
  return isMatch(value, "P", { locale });
}

/** Parses a locale short-date string; returns an Invalid Date if it doesn't match. */
export function parseShortDate(value: string, locale: Locale = de): Date {
  return parse(value, "P", new Date(), { locale });
}

/** Full weekday and date, e.g. "Montag, 22.06.2026". */
export function formatWeekdayDate(date: Date, locale: Locale = de): string {
  return format(date, "EEEE, dd.MM.yyyy", { locale });
}

/** Full weekday, date and time, e.g. "Montag, 22.06.2026 · 14:15 Uhr". */
export function formatWeekdayDateTime(date: Date, locale: Locale = de): string {
  return format(date, "EEEE, dd.MM.yyyy · HH:mm 'Uhr'", { locale });
}

/** Full month name and year, e.g. "Juni 2026". */
export function formatMonthYear(date: Date, locale: Locale = de): string {
  return format(date, "LLLL yyyy", { locale });
}

/** Short weekday and date, e.g. "Mo, 22.06.2026". */
export function formatShortWeekdayDate(date: Date, locale: Locale = de): string {
  return format(date, "EEEEEE, dd.MM.yyyy", { locale });
}
